
export const TripDisplayStatus = {
  extended_bonus: 'Extended Bonus',
  needs_attention: 'Needs Attention - Manual Bonus',
  in_progress: 'In Progress',
  finding_driver: 'Finding Driver',
  finalizing_details: 'Finalizing Details',
  completed: 'Completed',
  cancelled: 'Cancelled',
  unknown: 'Unknown Status - ping #tech-support',
};

export const TripStatus = {
  // No driver has accepted the trip
  processing: 'processing',
  // Driver accepted the trip, but hasn't been "matched"
  finalizing_details: 'finalizing_details',
  // Driver and riders are matched
  in_progress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled',
};

export const TripDisplayStatusOrder = [
  TripDisplayStatus.unknown,
  TripDisplayStatus.needs_attention,
  TripDisplayStatus.extended_bonus,
  TripDisplayStatus.finding_driver,
  TripDisplayStatus.in_progress,
  TripDisplayStatus.finalizing_details,
  TripDisplayStatus.completed,
  TripDisplayStatus.cancelled,
];

export const getTripDisplayStatus = (trip) => {
  if (trip.status === TripStatus.cancelled) {
    return TripDisplayStatus.cancelled;
  }
  if (trip.status === TripStatus.completed) {
    return TripDisplayStatus.completed;
  }
  if (trip.status === TripStatus.in_progress) {
    return TripDisplayStatus.in_progress;
  }

  if (trip.driver === null && trip.dispatchStatus === 'scheduled') {
    return TripDisplayStatus.finding_driver;
  }

  if (trip.status === TripStatus.finalizing_details) {
    return TripDisplayStatus.finalizing_details;
  }

  if (trip.dispatchStatus === 'extended_bonus') {
    return TripDisplayStatus.extended_bonus;
  }

  if (trip.dispatchStatus === 'attention') {
    return TripDisplayStatus.needs_attention;
  }

  return TripDisplayStatus.unknown;
};

export const getTripWaypointETA = (waypoint, tripEta) => {
  if (!tripEta) {
    return null;
  }
  const rideIds = waypoint.map((waypointItem) => waypointItem.ride_id);
  const rideEta = tripEta.rides.find((ride) => {
    const rideId = ride.rideId || ride.id;
    return rideIds.includes(rideId);
  });
  if (!rideEta) {
    return null;
  }
  const rideWaypoint = waypoint.find((waypointItem) => {
    const rideEtaId = rideEta.rideId || rideEta.id;
    return waypointItem.ride_id === rideEtaId;
  });
  const eta = rideWaypoint.type === 'pick-up' ? rideEta.pickUp.eta : rideEta.dropOff.eta;
  return eta;
};

export const getTripCurrentETA = (trip, tripEta) => {
  const currentWaypoint = trip.currentWaypoint;
  const waypoint = trip.waypoints[currentWaypoint] || [];
  return getTripWaypointETA(waypoint, tripEta);
};

export const isTripNational = (trip) => {
  const states = ['TX', 'FL'];
  return (
    (trip.originCity && !states.includes(trip.originCity.stateAbbr)) ||
    (trip.destinationCity && !states.includes(trip.destinationCity.stateAbbr))
  );
};

/**
 * The earliest departure time this trip was promised BEFORE dispatch started
 * delaying it, or null if it was never delayed.
 *
 * History entries written before the `reason` discriminator existed are all
 * dispatch delays, so a missing reason counts as one. Ops adjustments are
 * excluded: they are corrections, not slippage, and counting them would report
 * a deliberate reschedule as a dispatch failure (and a forward shift as a
 * negative delay).
 */
export const getDispatchDelayBaseline = (trip) => {
  let earliest = null;
  for (const ride of trip.rides || []) {
    const delays = (ride.departureTimeHistory || []).filter(
      (h) => h.reason !== 'ops_adjustment',
    );
    if (delays.length === 0) continue;
    const first = delays[0].pastDeptTime;
    if (!earliest || first < earliest) {
      earliest = first;
    }
  }
  return earliest;
};

/**
 * The earliest departure time recorded before ops adjusted this trip, or null
 * if ops never did. Rendered as a neutral note rather than a delay.
 */
export const getOpsAdjustmentBaseline = (trip) => {
  let earliest = null;
  for (const ride of trip.rides || []) {
    const adjustments = (ride.departureTimeHistory || []).filter(
      (h) => h.reason === 'ops_adjustment',
    );
    if (adjustments.length === 0) continue;
    const first = adjustments[0].pastDeptTime;
    if (!earliest || first < earliest) {
      earliest = first;
    }
  }
  return earliest;
};
